import React, { ChangeEvent, ClipboardEvent, FocusEvent, KeyboardEvent, useEffect, useMemo, useState } from 'react'
import block from 'bem-cn'

import { useMaskedValue, UseMaskedValueProps } from 'shared/hooks/useMaskedValue'
import { isUndefinedOrNull } from 'shared/utils/isUndefinedOrNull'

import {
  ControllerWrapper,
  ControllerWrapperSharedProps,
  ControllerWrapperSize,
} from 'components/UIKit/ControllerWrapper'
import { InputControls } from 'components/UIKit/Input/components'
import {
  CleanerValueFunctionSettings,
  FormatterValueFunctionSettings,
  useFormatter,
  useHidden,
  useValueRules,
  ValueRules,
} from 'components/UIKit/Input/hooks'
import { DefaultInputProps, InputType } from 'components/UIKit/Input/types'

import './Input.scss'

export type InputProps = ControllerWrapperSharedProps &
  Omit<DefaultInputProps, 'onChange' | 'size'> &
  Partial<Omit<UseMaskedValueProps, 'initialValue' | 'value'>> &
  Omit<CleanerValueFunctionSettings, 'value'> &
  Omit<FormatterValueFunctionSettings, 'value'> &
  ValueRules & {
    value?: string | number
    onChange: (value: string) => void
    canBeHidden?: boolean
    initialHidden?: boolean
    clearable?: boolean
    trimOnBlur?: boolean
    allowSpaces?: boolean
    customReplacer?: (value: string) => string
    onClickController?: () => void
  }

const TYPE_BY_TYPE = {
  number: 'text',
}

const ALLOWED_SPACES_BY_TYPE: Record<InputType, boolean> = {
  tel: false,
  number: false,
  search: true,
  email: false,
  password: false,
  text: true,
}

const b = block('input')

export const Input: React.FC<InputProps> = ({
  description,
  size = ControllerWrapperSize.MEDIUM,
  suffix,
  caption,
  variant,
  errorText,
  isError,
  isLoading,
  onClickController,
  prefix,
  className,
  value,
  markSymbol,
  mask,
  type = 'text',
  allowSpaces,
  maxLength,
  allowNegative,
  decimals,
  clearable,
  customReplacer,
  onChange,
  onFocus,
  trimOnBlur,
  onBlur,
  onKeyDown,
  disabled,
  required,
  withThousandSeparator,
  onPaste,
  canBeHidden,
  initialHidden,
  ...rest
}) => {
  const { formattedValue: formattedValueMask, cleanNewValue } = useMaskedValue({
    mask,
    markSymbol,
    value: String(value),
  })
  const { formatter, cleaner } = useFormatter({ type: type as InputType })
  const { applyRules } = useValueRules({
    type: type as InputType,
    cleaner,
  })
  const { isHidden, hide, reveal } = useHidden({ type: type as InputType, initialHidden })
  const [isFocused, setIsFocused] = useState(false)
  const isAllowedSpace = isUndefinedOrNull(allowSpaces) ? ALLOWED_SPACES_BY_TYPE[type as InputType] : allowSpaces
  const showSuffix = Boolean(suffix) || clearable || canBeHidden

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    let newValue = applyRules({
      value: e.target.value,
      maxLength,
      allowNegative,
      decimals,
    })

    newValue = cleanNewValue(newValue)

    if (customReplacer) {
      newValue = customReplacer(newValue)
    }

    onChange(newValue)
  }

  const currentType = useMemo(() => {
    if (isHidden) {
      return 'password'
    }

    if (type !== 'password') {
      return TYPE_BY_TYPE[type as keyof typeof TYPE_BY_TYPE] || type
    }

    return 'text'
  }, [type, isHidden])

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(true)
    onFocus?.(e)
  }

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false)
    if (trimOnBlur) {
      onChange(String(value).trim())
    }
    onBlur?.(e)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const isSpace = e.code === 'Space'

    if (isSpace && !isAllowedSpace) {
      e.preventDefault()
    }

    onKeyDown?.(e)
  }

  const onClear = () => {
    if (clearable) {
      onChange('')
    }
  }

  const formattedValue = useMemo(
    () =>
      formatter({
        value: formattedValueMask,
        withThousandSeparator,
      }),
    [formattedValueMask, withThousandSeparator, formatter],
  )

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    onPaste?.(e)
  }

  useEffect(() => {
    let newValue = applyRules({
      value: String(value),
      maxLength,
      allowNegative,
      decimals,
    })

    newValue = cleanNewValue(newValue)

    if (customReplacer) {
      newValue = customReplacer(newValue)
    }

    onChange(newValue)
  }, [mask])

  return (
    <ControllerWrapper
      className={b('input-wrapper')}
      caption={caption}
      size={size}
      variant={variant}
      suffix={
        showSuffix && (
          <>
            {suffix}
            <InputControls
              reveal={reveal}
              hide={hide}
              hasSuffix={Boolean(suffix)}
              onClear={onClear}
              value={String(value)}
              isHidden={isHidden}
              clearable={clearable}
              canBeHidden={canBeHidden}
            />
          </>
        )
      }
      prefix={prefix}
      errorText={errorText}
      isFocused={isFocused}
      isError={isError}
      disabled={disabled}
      required={required}
      onClick={onClickController}
      isLoading={isLoading}
      description={description}>
      <input
        className={b({ variant }).mix(className)}
        type={currentType}
        value={formattedValue}
        disabled={disabled}
        required={required}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        {...rest}
      />
    </ControllerWrapper>
  )
}
